// © 2026 Holmes Computer Consultants. All rights reserved. -- www.holmesconsultants.com
//
// The bookkeeping shared by every simulation-to-presentation feed.
//
// The simulation writes at 1000 Hz from inside its own hot path; a renderer reads at whatever
// rate it happens to run. Between them sits a bounded ring that overwrites its oldest entry,
// and every such feed needs the same four decisions made the same way:
//
//   * **Bounded, and it drops the oldest.** If nothing ever reads — a headless soak that
//     installed a feed and then never rendered — the memory is one fixed allocation rather
//     than a list of every contact in a two-thousand-minute run. Losing the oldest is also the
//     right loss for what reads this: a frame that arrives after a hundred and fifty entries
//     wants the hundred and fifty it can still see, not the first hundred and twenty-eight.
//   * **The cursor is a monotonic count**, not a slot. A consumer holding a slot index cannot
//     tell "nothing new" from "exactly one lap behind".
//   * **A consumer that falls behind is fast-forwarded**, and the gap is *counted* rather than
//     reported as an error. The frame those entries belonged to has already been drawn.
//   * **`Float64` for the count**, because `Int32` wraps after twenty-five days of continuous
//     simulation at 1000 Hz — which a soak reaches and a player does not, so the failure would
//     only ever appear where nobody is watching a screen. A JavaScript number is exact to 2⁵³.
//
// Extracted because there are now two feeds — events (M3-11) and contacts (M3-24) — and the
// drop accounting is exactly the sort of thing that is subtly wrong in the second copy. The
// columns stay with each feed, so both keep a typed visitor rather than sharing an untyped one.

/** Entries held before the oldest is overwritten. Two seconds of a very busy multiball. */
export const FEED_CAPACITY = 256

export class FeedRing {
  readonly capacity: number

  /** Entries ever written. Also the cursor a consumer that is up to date holds. */
  private written = 0

  /** Entries overwritten before anybody read them. A diagnostic, never a correction. */
  dropped = 0

  constructor(capacity: number = FEED_CAPACITY) {
    this.capacity = Math.max(1, Math.floor(capacity))
  }

  /** The slot the next entry goes in. Advances the ring, so call it once per write. */
  claim(): number {
    const slot = this.written % this.capacity
    this.written++
    return slot
  }

  /** The cursor of a consumer that has seen everything. Pass to the next read. */
  get cursor(): number {
    return this.written
  }

  /** Entries written since the ring was created, including any already overwritten. */
  get recorded(): number {
    return this.written
  }

  /**
   * The first index a consumer holding `cursor` can still be shown.
   *
   * Charges anything it has missed to `dropped`. Read up to `cursor` from here.
   */
  begin(cursor: number): number {
    const oldest = Math.max(0, this.written - this.capacity)
    if (cursor < oldest) {
      this.dropped += oldest - cursor
      return oldest
    }
    return cursor
  }

  /** Where entry `index` lives. */
  slotOf(index: number): number {
    return index % this.capacity
  }

  /**
   * Forget everything, as when the machine is rebuilt.
   *
   * The step numbers in the ring belong to a simulation that no longer exists, and a consumer
   * holding a cursor from it would either replay a dead machine's entries or skip the new
   * machine's.
   */
  clear(): void {
    this.written = 0
    this.dropped = 0
  }
}
